import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { wishlistKeys } from './queryKeys'
import { wishlistApi } from '../../lib'

import type { WishlistData } from '../../lib/apiClient'
import type { WishlistItem } from '../../types'

interface UseWishlistQueryResult {
  wishlistId: string | null
  wishlistItems: WishlistItem[]
  itemsCount: number
  isLoggedIn: boolean
  refetch: () => void
}

export function useWishlistQuery(): UseWishlistQueryResult {
  const { data: wishlistData, isSuccess, refetch: queryRefetch } = useQuery<WishlistData>({
    queryKey: wishlistKeys.customer(),
    queryFn: () => wishlistApi.getCustomerWishlist(),
    retry: false, // Override default (1) - don't retry auth-dependent query
  })

  // Wrap refetch to return void (ignoring the Promise)
  const refetch = useCallback(() => {
    void queryRefetch()
  }, [queryRefetch])

  // Derive state directly from query data (no useEffect + useState)
  const { wishlistId, wishlistItems, itemsCount, isLoggedIn } = useMemo(() => {
    const wishlist = wishlistData?.customer?.wishlist

    return {
      wishlistId: wishlist?.id ?? null,
      wishlistItems: wishlist?.items_v2?.items ?? [],
      itemsCount: wishlist?.items_count ?? 0,
      isLoggedIn: isSuccess && !!wishlist,
    }
  }, [wishlistData, isSuccess])

  return {
    wishlistId,
    wishlistItems,
    itemsCount,
    isLoggedIn,
    refetch,
  }
}
